<template>
    <div :class="{ container: !upgrade }">
        <h4 class="text-uppercase mt-5">Your subscription or trial has ended</h4>
        <div v-if="switchOrganization" class="col-12 col-sm-7 mb-2">
            Were you trying to login to <b>{{ switchOrganization }}</b
            >? Click on your name in the upper right corner to switch to other organizations.
        </div>
        <hr class="bar mb-5" />

        <template>
            <div v-show="reactivate" class="col-12 col-sm-7">
                <h3 class="text-primary">Select your billing cycle</h3>
                <form>
                    <fieldset class="form-group">
                        <div class="row">
                            <div class="col-12">
                                <div class="form-check" v-for="p in plans">
                                    <input
                                        class="form-check-input"
                                        type="radio"
                                        :id="p.id"
                                        v-model="plan"
                                        :value="p.id"
                                    />
                                    <label class="form-check-label" :for="p.id">
                                        {{ p.nickname }}
                                        <span
                                            >(${{ p.amount.toFixed(2) }} per seat per
                                            {{ p.interval }} plus sales tax where applicable)</span
                                        >
                                    </label>
                                </div>
                            </div>
                        </div>
                    </fieldset>
                </form>

                <h4>Enter your billing details</h4>
                <div class="bg-white border-light p-3 mt-2 mb-3 paymentf">
                    <div class="form-group">
                        <div class="row">
                            <div class="col mb-3">
                                <label>Cardholder name</label>
                                <input
                                    id="cardholder-name"
                                    v-model="cardholderName"
                                    class="form-control"
                                    placeholder="Cardholder name"
                                />
                            </div>
                        </div>
                        <div class="row">
                            <div class="col">
                                <label>Card Information</label>
                                <div id="stripe-card" class="form-control"></div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="bg-white border-light p-3 mt-2 mb-3 paymentf">
                    <coupon-widget
                        :user="user"
                        @valid-coupon="handleValidCoupon"
                        @invalid-coupon="handleInvalidCoupon"
                        @safeToSubmitChange="handleCouponSafeToSubmit"
                    ></coupon-widget>
                </div>

                <button
                    class="btn btn-primary font-weight-bold text-white mb-4"
                    @click.prevent="checkout()"
                    id="upgrade"
                >
                    Checkout
                </button>

                <p
                    class="mb-0 text-primary cursor-pointer float-right"
                    @click="exportData()"
                    :disbled="loadingExport"
                >
                    Or, click here to export your data.
                    <i v-if="loadingExport" class="fa fa-circle-o-notch fa-spin"></i>
                </p>

                <div v-if="errorMsg" class="alert alert-danger">
                    <a href="#" class="close" data-dismiss="alert">&times;</a>
                    <strong>Error!</strong> {{ errorMsg }}
                </div>
            </div>
        </template>

        <template v-if="!reactivate && organization && billingUser">
            <p>
                The {{ organization.name }} account is disabled. Please contact
                {{ billingUser }} for assistance.
            </p>
        </template>
    </div>
</template>

<script>
import { helpers } from "../plugins/helpers";
import CouponWidget from "./widgets/CouponWidget";

export default {
    name: "Billing",
    props: ["user", "organization", "billingUser", "upgrade", "switchOrganization", "intent"],
    components: { CouponWidget },
    data() {
        return {
            reactivate: false,
            cardholderName: "",
            plan: null,
            plans: null,
            loading: false,
            coupon: null,
            errorMsg: null,
            isSafeToSubmitCoupon: true,
            didUserAttemptCheckout: false,
            loadingExport: false,
        };
    },
    created() {
        this.getBasicPlans();
    },
    mounted() {
        this.checkReactivate();
        this.$emit("hide-navigation", true);
    },
    methods: {
        getBasicPlans() {
            axios.get(`/api/plans`).then(r => {
                this.plans = r.data;
            });
        },

        checkReactivate() {
            const urlParams = new URLSearchParams(window.location.search);
            if (urlParams.get("reactivate")) {
                this.reactivate = true;
                helpers.mountStripeElement();
            }
        },

        async checkout() {
            this.didUserAttemptCheckout = true;

            // make sure user has validated their validCoupon if it exists
            if (!this.isSafeToSubmitCoupon) {
                this.loading = false;
                this.errorMsg =
                    "You must verify the coupon before continuing. Remove the coupon if you won't be using it.";
                return;
            }

            if (!this.plan) {
                this.errorMsg = "You must select a plan in order to continue.";
                return;
            }

            helpers.toggleSpinner("upgrade");

            const { setupIntent, error } = await stripe.handleCardSetup(
                this.intent.client_secret,
                card,
                {
                    payment_method_data: {
                        billing_details: { name: this.cardholderName },
                    },
                }
            );

            if (error) {
                helpers.toggleSpinner("upgrade");
                this.$swal("Error", `Something went wrong authorizing your card.`, "error");
            } else {
                this.createSubscription(setupIntent);
            }
        },

        handleValidCoupon(coupon) {
            this.coupon = coupon;
            this.errorMsg = null;
        },

        handleInvalidCoupon() {
            this.coupon = null;
        },

        handleCouponSafeToSubmit(isSafe) {
            this.isSafeToSubmitCoupon = isSafe;
            this.errorMsg = null;
        },

        createSubscription(setupIntent) {
            let payload = {
                setupIntent,
                plan: this.plan,
                cardholderName: this.cardholderName,
            };
            if (this.coupon) {
                payload.coupon = this.coupon;
            }
            axios
                .post(`/api/users/${this.user.id}/subscription/new`, payload)
                .then(r => {
                    window.location = "/account";
                })
                .catch(e => {
                    helpers.toggleSpinner("upgrade");
                    console.error(e);
                });
        },

        exportData() {
            this.loadingExport = true;
            this.error = null;
            axios({
                url: `/users/${this.user.id}/export`,
                method: "GET",
                responseType: "blob", // important
            })
                .then(response => {
                    this.loadingExport = false;
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement("a");
                    link.href = url;
                    link.setAttribute(
                        "download",
                        `Export ${this.user.first_name} ${this.user.last_name}.xlsx`
                    );
                    document.body.appendChild(link);
                    link.click();
                })
                .catch(e => {
                    console.error(e);
                    this.error = "No data to download";
                    this.loadingExport = false;
                });
        },
    },
};
</script>

<style scoped>
.pricing-plan {
    border: solid 1px #d64571;
    flex-basis: 0;
    flex-grow: 1;
}
.save-tag {
    position: absolute;
    right: -1rem;
    top: 1rem;
    z-index: 2;
    background-color: #fff1c3;
}
</style>
